import React, { useState, useEffect } from 'react';
import { LedgerStats, ForumPost } from '../types';

const Commons: React.FC = () => {
  const [stats, setStats] = useState<LedgerStats>({
    votesOfInterest: 842,
    investigativeFund: 4200,
    thresholdVotes: 1000,
    thresholdFund: 5000,
    daysRemaining: 18
  });

  const [posts, setPosts] = useState<ForumPost[]>([
    {
      id: '1',
      author: 'Ex-Regulator_UK',
      title: 'Evidence of Hidden Weighting in Claim Denial Algorithms',
      content: 'I am sharing an internal memo discussing the implementation of "Risk Suppression" variables in the 2023 AI update...',
      timestamp: new Date(),
      votes: 156,
      tags: ['AI Governance', 'Insurance']
    },
    {
      id: '2',
      author: 'Sentinel_09',
      title: 'Geneva Corridor: Tracking the Drift from National Mandates',
      content: 'A forensic comparison of recent treaty language vs. domestic legislative implementation suggests a 15% drift in accountability standards...',
      timestamp: new Date(),
      votes: 92,
      tags: ['Regulatory Drift', 'Geneva']
    }
  ]);

  const [newPost, setNewPost] = useState({ author: '', title: '', content: '', tags: '' });
  const [showPostModal, setShowPostModal] = useState(false);

  useEffect(() => {
    // Fetch posts from API when available
    const fetchPosts = async () => {
      try {
        const response = await fetch('/api/forum/posts');
        if (response.ok) {
          const data = await response.json();
          setPosts(data.map((p: any) => ({
            ...p,
            timestamp: new Date(p.created_at),
            tags: p.tags ? p.tags.split(',') : []
          })));
        }
      } catch (error) {
        // Keep default posts
      }
    };
    fetchPosts();
  }, []);

  const handleSubmitPost = async () => {
    if (!newPost.author || !newPost.title || !newPost.content) return;
    
    const post: ForumPost = {
      id: Date.now().toString(),
      author: newPost.author,
      title: newPost.title,
      content: newPost.content,
      timestamp: new Date(),
      votes: 0,
      tags: newPost.tags.split(',').map(t => t.trim()).filter(Boolean)
    };

    // Try to save to API
    try {
      await fetch('/api/forum/posts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(post)
      });
    } catch (error) {
      // Continue anyway for local state
    }

    setPosts([post, ...posts]);
    setNewPost({ author: '', title: '', content: '', tags: '' });
    setShowPostModal(false);
  };

  const handleVote = async (postId: string) => {
    setPosts(posts.map(p => 
      p.id === postId ? { ...p, votes: p.votes + 1 } : p
    ));
    setStats(prev => ({ ...prev, votesOfInterest: prev.votesOfInterest + 1 }));
  };

  const fundProgress = (stats.investigativeFund / stats.thresholdFund) * 100;
  const voteProgress = (stats.votesOfInterest / stats.thresholdVotes) * 100;
  const isThresholdMet = stats.votesOfInterest >= stats.thresholdVotes && stats.investigativeFund >= stats.thresholdFund;

  return (
    <div className="py-24 bg-white">
      {/* Post Modal */}
      {showPostModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-black mb-6 uppercase tracking-tighter">Submit Briefing</h3>
            
            <div className="space-y-6">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-2">Anonymous Handle</label>
                <input
                  type="text"
                  value={newPost.author}
                  onChange={(e) => setNewPost({ ...newPost, author: e.target.value })}
                  placeholder="e.g., Whistleblower_Alpha"
                  className="w-full p-4 border border-slate-200 font-medium"
                />
              </div>
              
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-2">Briefing Title</label>
                <input
                  type="text"
                  value={newPost.title}
                  onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                  placeholder="Concise description of the anomaly"
                  className="w-full p-4 border border-slate-200 font-medium"
                />
              </div>
              
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-2">Content</label>
                <textarea
                  value={newPost.content}
                  onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                  placeholder="Provide factual details, dates, entities involved..."
                  rows={6}
                  className="w-full p-4 border border-slate-200 font-medium resize-none"
                />
              </div>
              
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-2">Tags (comma-separated)</label>
                <input
                  type="text"
                  value={newPost.tags}
                  onChange={(e) => setNewPost({ ...newPost, tags: e.target.value })}
                  placeholder="e.g., Insurance, AI, Regulatory"
                  className="w-full p-4 border border-slate-200 font-medium"
                />
              </div>
            </div>
            
            <div className="flex gap-4 mt-8">
              <button
                onClick={handleSubmitPost}
                className="flex-1 py-4 bg-black text-white font-bold text-xs uppercase tracking-widest"
              >
                Submit Briefing
              </button>
              <button
                onClick={() => setShowPostModal(false)}
                className="flex-1 py-4 border border-slate-200 font-bold text-xs uppercase tracking-widest"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mb-20">
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-slate-400 mb-8">Professional digital commons</div>
          <h1 className="text-6xl font-black text-slate-900 mb-8 tracking-tighter">The Commons</h1>
          <p className="text-xl text-slate-500 font-medium leading-relaxed">
            A professional digital commons for the exchange of lived experiences, eyewitness accounts, and data-driven observations regarding institutional failure.
          </p>
        </div>

        {/* Transparency Ledger */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mb-20">
          <div className="lg:col-span-1 p-10 bg-slate-900 text-white rounded-none border-t-[12px] border-slate-600">
            <h3 className="text-[10px] font-bold uppercase tracking-[0.3em] text-slate-500 mb-8">Transparency Ledger</h3>
            
            <div className="mb-10">
              <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest mb-2">
                <span>Votes of Interest</span>
                <span>{stats.votesOfInterest} / {stats.thresholdVotes}</span>
              </div>
              <div className="h-1.5 w-full bg-slate-800">
                <div className="h-full bg-white transition-all duration-1000" style={{ width: `${Math.min(voteProgress, 100)}%` }}></div>
              </div>
            </div>

            <div className="mb-10">
              <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest mb-2">
                <span>Investigative Fund</span>
                <span>${stats.investigativeFund} / ${stats.thresholdFund}</span>
              </div>
              <div className="h-1.5 w-full bg-slate-800">
                <div className="h-full bg-green-500 transition-all duration-1000" style={{ width: `${Math.min(fundProgress, 100)}%` }}></div>
              </div>
            </div>

            {isThresholdMet ? (
              <div className="p-4 bg-green-900/50 border border-green-500 text-green-400 text-xs font-bold uppercase tracking-widest flex items-center space-x-3 mb-6">
                <i className="fas fa-certificate text-lg"></i>
                <span>Formal Notice of Inquiry Issued</span>
              </div>
            ) : (
              <div className="text-xs font-medium text-slate-500 mb-6">
                Threshold remaining: {Math.max(stats.thresholdVotes - stats.votesOfInterest, 0)} votes and ${Math.max(stats.thresholdFund - stats.investigativeFund, 0)} in contributions.
              </div>
            )}

            <div className="pt-6 border-t border-slate-800">
              <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-widest">
                <span>Institution Response Timer</span>
                <span className="text-red-400">{stats.daysRemaining} Days</span>
              </div>
              <p className="text-[9px] text-slate-600 mt-2 uppercase">Targeted institution must respond before audit publication.</p>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-black uppercase tracking-tighter">Recent Briefings</h2>
              <button 
                onClick={() => setShowPostModal(true)}
                className="px-6 py-2 bg-black text-white text-[10px] font-black uppercase tracking-[0.2em] hover:bg-slate-800 transition-all"
              >
                Post Briefing
              </button>
            </div>

            {posts.map(post => (
              <div key={post.id} className="p-8 border border-slate-200 hover:border-black transition-all group">
                <div className="flex items-center space-x-3 mb-4 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                  <span className="text-slate-900">{post.author}</span>
                  <span>•</span>
                  <span>{post.timestamp.toLocaleDateString()}</span>
                  <span>•</span>
                  <div className="flex space-x-2">
                    {post.tags.map(tag => <span key={tag} className="bg-slate-50 px-2 py-0.5">{tag}</span>)}
                  </div>
                </div>
                <h4 className="text-xl font-black mb-4 group-hover:text-slate-600 transition-colors">{post.title}</h4>
                <p className="text-sm text-slate-500 leading-relaxed mb-6">{post.content}</p>
                <div className="flex items-center space-x-6">
                  <button 
                    onClick={() => handleVote(post.id)}
                    className="text-[10px] font-bold uppercase tracking-widest flex items-center space-x-2 hover:text-black text-slate-400"
                  >
                    <i className="fas fa-arrow-up"></i>
                    <span>Interest: {post.votes}</span>
                  </button>
                  <button className="text-[10px] font-bold uppercase tracking-widest hover:text-black text-slate-400">Share Securely</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Legal Disclosure */}
        <div className="mt-32 p-10 bg-slate-50 border-t border-slate-200">
          <h3 className="text-xs font-black uppercase tracking-[0.3em] mb-4">Section 230 Disclosure</h3>
          <p className="text-xs text-slate-500 leading-relaxed max-w-4xl">
            In accordance with 47 U.S.C. § 230, System Audit provides an interactive computer service that enables the exchange of information created by third-party users. System Audit is not the "publisher" of content provided by independent contributors and shall not be held liable for any such content. We advocate for the uncorrupted flow of information as protected by the First Amendment.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Commons;
